import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'wouter';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Martini, Utensils, ArrowLeft, Loader2, AlertCircle, Send, ExternalLink, Smartphone, Globe, TabletSmartphone, Star, Plus, UserPlus, UserMinus, MapPin, Calendar, Pencil, Beer, Trash2, Search, BellRing } from 'lucide-react';
import {
  useAdminConversationsListener,
  useAdminMessagesListener,
  useAdminChatUserDetails,
  useSendAdminSystemMessage,
  useToggleConversationStar,
  useRemoveChatParticipants,
  useDeleteChat,
  useDeleteAllGroupChats,
  useResendCloseOutChatBroadcast,
  AdminConversation,
  AdminMessage,
} from '../api/useAdminChats';
import { SendMessageButton } from '../components/SendMessageButton';
import { CreateGroupChatModal, AddChatParticipantsModal, ChatInfoModal, CreateCloseOutChatModal } from '../components/GroupChatModals';
import { useToast } from '@/components/ux/Toast';

function formatTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' });
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function DevicePlatformIcons({ platforms }: { platforms?: string[] }) {
  if (!platforms || platforms.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-0.5 ml-1">
      {platforms.includes('ios') && (
        <span title="iOS">
          <TabletSmartphone className="w-3 h-3 text-blue-500" />
        </span>
      )}
      {platforms.includes('android') && (
        <span title="Android">
          <Smartphone className="w-3 h-3 text-green-500" />
        </span>
      )}
      {platforms.includes('web') && (
        <span title="Web">
          <Globe className="w-3 h-3 text-amber-500" />
        </span>
      )}
    </span>
  );
}

// Conversation List Item
function ConversationItem({
  conversation,
  isSelected,
  userDetails,
  onSelect,
  onToggleStar,
  isTogglingStar,
}: {
  conversation: AdminConversation;
  isSelected: boolean;
  userDetails: Record<string, { name: string; email?: string; hasDevice?: boolean; devicePlatforms?: string[] }>;
  onSelect: () => void;
  onToggleStar: () => void;
  isTogglingStar: boolean;
}) {
  const isTableChat = !!conversation.tableId;
  const isGroupChat = conversation.kind === 'group';
  const isStarred = conversation.starred === true;

  // Get participant names
  const participantNames = conversation.participants
    .map((id) => userDetails[id]?.name || conversation.participantDetails[id]?.name || 'Unknown')
    .join(', ');

  const title = conversation.title || (isGroupChat ? 'Group chat' : participantNames);

  // Group chats don't track unread counts, so this is always 0 for them.
  const participantsWithUnread = isGroupChat
    ? 0
    : Object.values(conversation.unreadCounts || {}).filter((count) => count > 0).length;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={`w-full p-4 flex items-start gap-3 border-b border-dark-border/50 dark:border-neutral-700/50 text-left transition-colors cursor-pointer ${
        isSelected ? 'bg-primary-500/10' : 'hover:bg-dark-border/30 dark:hover:bg-neutral-700/30'
      }`}
    >
      {/* Icon */}
      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
        isGroupChat ? 'bg-secondary-500/20' : 'bg-primary-500/20'
      }`}>
        {isTableChat ? (
          <Utensils className="w-5 h-5 text-primary-500" />
        ) : isGroupChat ? (
          <Martini className="w-5 h-5 text-secondary-500" />
        ) : (
          <Martini className="w-5 h-5 text-primary-500" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <h4 className="font-semibold truncate dark:text-neutral-100">{title}</h4>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!isTogglingStar) onToggleStar();
            }}
            disabled={isTogglingStar}
            aria-label={isStarred ? 'Unstar conversation' : 'Star conversation'}
            title={isStarred ? 'Unstar' : 'Star'}
            className="flex-shrink-0 p-1 -m-1 rounded hover:bg-dark-border/40 dark:hover:bg-neutral-700/60 disabled:opacity-50 transition-colors"
          >
            <Star
              className={`w-4 h-4 ${
                isStarred
                  ? 'text-yellow-500 fill-yellow-500'
                  : 'text-dark-muted/50 dark:text-neutral-500'
              }`}
            />
          </button>
        </div>
        {conversation.subtitle && (
          <p className="text-xs text-dark-muted dark:text-neutral-400 truncate">{conversation.subtitle}</p>
        )}
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <p className="text-sm text-dark-muted dark:text-neutral-400 truncate">
            {conversation.lastMessage?.text || 'No messages yet'}
          </p>
          {conversation.lastMessage?.createdAt && (
            <span className="text-xs text-dark-muted dark:text-neutral-500 flex-shrink-0">
              {formatTime(conversation.lastMessage.createdAt)}
            </span>
          )}
        </div>
        <p className="text-xs text-dark-muted/70 dark:text-neutral-500 mt-1">
          {conversation.participants.length} participants
          {participantsWithUnread > 0 && (
            <span className="text-type-600 dark:text-type-300"> · {participantsWithUnread} with unread</span>
          )}
        </p>
      </div>
    </div>
  );
}

// Message Bubble
function MessageBubble({
  message,
  senderName,
  showSender,
}: {
  message: AdminMessage;
  senderName: string;
  showSender: boolean;
}) {
  if (message.type === 'system') {
    return (
      <div className="flex justify-center">
        <span className="text-xs text-dark-muted dark:text-neutral-300 bg-primary-500/30 px-3 py-1 rounded-full">
          {message.content}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full overflow-hidden">
      {showSender && (
        <span className="text-xs text-dark-muted dark:text-neutral-400 mb-1 ml-1">{senderName}</span>
      )}
      <div className="bg-white dark:bg-neutral-700 rounded-2xl px-4 py-2 mr-auto max-w-[100%]">
        <p className="text-sm whitespace-pre-wrap break-words dark:text-neutral-100">{message.content}</p>
        <span className="text-[10px] text-dark-muted dark:text-neutral-400 mt-1 block">
          {formatDateTime(message.createdAt)}
        </span>
      </div>
    </div>
  );
}

// Conversation View
function ConversationView({
  conversation,
  userDetails,
  onBack,
}: {
  conversation: AdminConversation;
  userDetails: Record<string, { name: string; email?: string; hasDevice?: boolean; devicePlatforms?: string[] }>;
  onBack: () => void;
}) {
  const { messages, isLoading } = useAdminMessagesListener(conversation.id);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [messageInput, setMessageInput] = useState('');
  const [isAddUsersOpen, setIsAddUsersOpen] = useState(false);
  const [isEditChatInfoOpen, setIsEditChatInfoOpen] = useState(false);
  const sendSystemMessage = useSendAdminSystemMessage();
  const removeParticipants = useRemoveChatParticipants();
  const deleteChat = useDeleteChat();
  const resendCloseOutBroadcast = useResendCloseOutChatBroadcast();
  const toast = useToast();

  const isGroupChat = conversation.kind === 'group';
  const title = conversation.title || (isGroupChat
    ? 'Group chat'
    : conversation.participants
        .map((id) => userDetails[id]?.name || 'Unknown')
        .join(', '));

  const handleDeleteChat = async () => {
    if (deleteChat.isPending) return;
    if (
      !window.confirm(
        `This will permanently delete "${title}" and all of its messages. This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      const res = await deleteChat.mutateAsync({ conversationId: conversation.id });
      if (!res.success) {
        toast.error(res.error || 'Failed to delete chat');
        return;
      }
      toast.success('Chat deleted');
      onBack();
    } catch (err) {
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : 'Failed to delete chat';
      toast.error(message);
    }
  };

  const handleResendCloseOutBroadcast = async () => {
    if (resendCloseOutBroadcast.isPending) return;
    if (
      !window.confirm(
        `Resend the close-out notification to ${conversation.participants.length} participant${conversation.participants.length === 1 ? '' : 's'}?`
      )
    ) {
      return;
    }
    try {
      const res = await resendCloseOutBroadcast.mutateAsync(conversation.id);
      if (!res.success) {
        toast.error(res.error || 'Failed to resend close-out notification');
        return;
      }
      toast.success(`Resent close-out notification to ${res.data?.participantCount ?? 0} participants`);
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to resend close-out notification';
      toast.error(message);
    }
  };

  const handleRemoveParticipant = async (userId: string) => {
    if (removeParticipants.isPending) return;
    const name = userDetails[userId]?.name || 'this user';
    if (!window.confirm(`Remove ${name} from this chat?`)) return;
    try {
      const res = await removeParticipants.mutateAsync({
        conversationId: conversation.id,
        userIds: [userId],
      });
      if (!res.success) {
        toast.error(res.error || 'Failed to remove user');
        return;
      }
      toast.success(`Removed ${name}`);
    } catch (err) {
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : 'Failed to remove user';
      toast.error(message);
    }
  };

  const getSenderName = (senderId: string) => {
    return userDetails[senderId]?.name || conversation.participantDetails[senderId]?.name || 'Unknown';
  };

  const handleSendMessage = async () => {
    const content = messageInput.trim();
    if (!content || sendSystemMessage.isPending) return;

    try {
      await sendSystemMessage.mutateAsync({
        conversationId: conversation.id,
        content,
      });
      setMessageInput('');
    } catch (error) {
      console.error('Failed to send system message:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden rounded-lg">
      {/* Header */}
      <div className="p-4 border-b border-dark-border dark:border-neutral-700 flex items-center gap-3">
        <button
          onClick={onBack}
          className="md:hidden p-2 -ml-2 hover:bg-dark-border/30 dark:hover:bg-neutral-700/30 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 dark:text-neutral-200" />
        </button>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          isGroupChat ? 'bg-secondary-500/20' : 'bg-primary-500/20'
        }`}>
          {conversation.tableId ? (
            <Utensils className="w-5 h-5 text-primary-500" />
          ) : isGroupChat ? (
            <Martini className="w-5 h-5 text-secondary-500" />
          ) : (
            <Martini className="w-5 h-5 text-primary-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold truncate dark:text-neutral-100">{title}</h3>
          {conversation.subtitle && (
            <p className="text-xs text-dark-muted dark:text-neutral-400">{conversation.subtitle}</p>
          )}
          {isGroupChat && (
            <p className="text-xs text-dark-muted dark:text-neutral-400">
              Group chat · {conversation.participants.length} member{conversation.participants.length === 1 ? '' : 's'}
            </p>
          )}
        </div>
        {isGroupChat && (
          <button
            onClick={() => setIsAddUsersOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            title="Add users to this chat"
          >
            <UserPlus className="w-3.5 h-3.5" /> Add users
          </button>
        )}
        <button
          onClick={() => setIsEditChatInfoOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-border/30 dark:bg-neutral-700 hover:bg-dark-border/50 dark:hover:bg-neutral-600 rounded-lg transition-colors text-dark-muted dark:text-neutral-300 hover:text-primary-500"
          title={conversation.chatInfo ? 'Edit chat info' : 'Add chat info (venue + meeting time)'}
        >
          <Pencil className="w-3.5 h-3.5" />
          {conversation.chatInfo ? 'Info' : 'Add info'}
        </button>
        {conversation.chatInfo && (
          <button
            onClick={handleResendCloseOutBroadcast}
            disabled={resendCloseOutBroadcast.isPending || conversation.participants.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-border/30 dark:bg-neutral-700 hover:bg-dark-border/50 dark:hover:bg-neutral-600 rounded-lg transition-colors text-dark-muted dark:text-neutral-300 hover:text-primary-500 disabled:opacity-50"
            title="Resend the close-out notification (broadcast 65) to all participants"
          >
            {resendCloseOutBroadcast.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <BellRing className="w-3.5 h-3.5" />
            )}
            Resend notification
          </button>
        )}
        {conversation.tableId && conversation.eventTimeId && (
          <a
            href={`/admin/matching/${conversation.eventTimeId}?q=${encodeURIComponent(conversation.tableId)}`}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-border/30 dark:bg-neutral-700 hover:bg-dark-border/50 dark:hover:bg-neutral-600 rounded-lg transition-colors text-dark-muted dark:text-neutral-300 hover:text-primary-500"
            title="Open this table on the matching page"
          >
            <Utensils className="w-3.5 h-3.5" />
            Matching
          </a>
        )}
        <a
          href={`https://console.firebase.google.com/u/0/project/gayborhood-471323/firestore/databases/chat/data/~2Fconversations~2F${conversation.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-3 py-1.5 text-xs bg-dark-border/30 dark:bg-neutral-700 hover:bg-dark-border/50 dark:hover:bg-neutral-600 rounded-lg transition-colors text-dark-muted dark:text-neutral-300 hover:text-primary-500"
          title="Open in Firestore"
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
        <SendMessageButton
          userIds={conversation.participants}
          recipientLabel={`${conversation.participants.length} participant${conversation.participants.length !== 1 ? 's' : ''}`}
          variant="compact"
          disabled={conversation.participants.length === 0}
        />
        <button
          onClick={handleDeleteChat}
          disabled={deleteChat.isPending}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-border/30 dark:bg-neutral-700 hover:bg-red-500/20 dark:hover:bg-red-500/20 rounded-lg transition-colors text-dark-muted dark:text-neutral-300 hover:text-red-500 disabled:opacity-50"
          title="Delete this chat and all its messages"
          aria-label="Delete chat"
        >
          {deleteChat.isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Trash2 className="w-3.5 h-3.5" />
          )}
        </button>
      </div>

      {/* Chat Info */}
      {conversation.chatInfo && (
        <div className="px-4 py-2 bg-dark-border/20 dark:bg-neutral-800/50 border-b border-dark-border dark:border-neutral-700">
          <div className="text-xs text-dark-muted dark:text-neutral-400 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {conversation.chatInfo.venue.googleMapsLink ? (
                <a
                  href={conversation.chatInfo.venue.googleMapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary-500 hover:underline"
                >
                  {conversation.chatInfo.venue.name}
                </a>
              ) : (
                <span>{conversation.chatInfo.venue.name}</span>
              )}
              {conversation.chatInfo.venue.address && (
                <span className="text-dark-muted/70 dark:text-neutral-500">· {conversation.chatInfo.venue.address}</span>
              )}
            </span>
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDateTime(conversation.chatInfo.meetingTime)}
            </span>
          </div>
          {conversation.chatInfo.description?.trim() && (
            <p className="text-xs text-dark-muted dark:text-neutral-400 whitespace-pre-wrap mt-1">
              {conversation.chatInfo.description.trim()}
            </p>
          )}
        </div>
      )}

      {/* Participants Info */}
      <div className="px-4 py-2 bg-dark-border/20 dark:bg-neutral-800/50 border-b border-dark-border dark:border-neutral-700">
        {conversation.participants.length === 0 ? (
          <p className="text-xs text-dark-muted dark:text-neutral-400">
            <span className="font-medium">Participants:</span> none yet
          </p>
        ) : (
          <div className="text-xs text-dark-muted dark:text-neutral-400 flex flex-wrap items-center gap-x-1 gap-y-1.5">
            <span className="font-medium">Participants:</span>
            {conversation.participants.map((id) => {
              const unreadCount = isGroupChat ? 0 : conversation.unreadCounts?.[id] || 0;
              return (
                <span key={id} className="inline-flex items-center gap-1">
                  <a href={`/admin/users/?q=${id}`} className="inline-flex items-center gap-1 hover:underline">
                    <span>
                      {userDetails[id]?.name || 'Unknown'}
                      {userDetails[id]?.email && (
                        <span className="text-dark-muted/70 dark:text-neutral-500"> ({userDetails[id].email})</span>
                      )}
                    </span>
                    <DevicePlatformIcons platforms={userDetails[id]?.devicePlatforms} />
                    {unreadCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] bg-red-500 text-white text-[9px] font-bold rounded-full px-1">
                        {unreadCount}
                      </span>
                    )}
                  </a>
                  {isGroupChat && (
                    <button
                      onClick={() => handleRemoveParticipant(id)}
                      disabled={removeParticipants.isPending}
                      className="p-0.5 -m-0.5 rounded hover:bg-red-500/20 text-dark-muted hover:text-red-500 disabled:opacity-50 transition-colors"
                      title="Remove from chat"
                      aria-label="Remove from chat"
                    >
                      <UserMinus className="w-3 h-3" />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-dark-muted dark:text-neutral-400 py-8">No messages yet</div>
        ) : (
          <>
            {messages.map((message, index) => {
              const showSender = index === 0 || messages[index - 1].senderId !== message.senderId;
              return (
                <MessageBubble
                  key={message.id}
                  message={message}
                  senderName={getSenderName(message.senderId)}
                  showSender={showSender}
                />
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* System Message Input */}
      <div className="p-4 border-t border-dark-border dark:border-neutral-700 bg-white dark:bg-neutral-800">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Send system message..."
            className="flex-1 px-4 py-2 rounded-full bg-dark-border/30 dark:bg-neutral-700 border border-dark-border/50 dark:border-neutral-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:text-neutral-100 dark:placeholder-neutral-400 text-sm"
            disabled={sendSystemMessage.isPending}
          />
          <button
            onClick={handleSendMessage}
            disabled={!messageInput.trim() || sendSystemMessage.isPending}
            className="p-2 rounded-full bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sendSystemMessage.isPending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        <p className="text-xs text-dark-muted dark:text-neutral-500 mt-2 text-center">
          {isGroupChat
            ? 'Group chats: no notifications, no unread tracking — messages just appear in the room.'
            : 'Messages sent here appear as system notifications to all participants'}
        </p>
      </div>

      {isAddUsersOpen && (
        <AddChatParticipantsModal
          conversationId={conversation.id}
          conversationTitle={title}
          existingParticipantIds={conversation.participants}
          onClose={() => setIsAddUsersOpen(false)}
          onAdded={() => {
            // Real-time listener picks up the change automatically.
          }}
        />
      )}

      {isEditChatInfoOpen && (
        <ChatInfoModal
          conversationId={conversation.id}
          initialTitle={conversation.title || title}
          initialChatInfo={conversation.chatInfo}
          onClose={() => setIsEditChatInfoOpen(false)}
        />
      )}
    </div>
  );
}

// Empty State
function EmptyState() {
  return (
    <div className="flex-1 hidden md:flex items-center justify-center bg-white dark:bg-neutral-800 rounded-lg">
      <div className="text-center">
        <MessageCircle className="w-16 h-16 text-dark-muted/30 dark:text-neutral-600 mx-auto mb-4" />
        <p className="text-dark-muted dark:text-neutral-400">Select a conversation to view messages</p>
      </div>
    </div>
  );
}

// Error State
function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-500 font-medium">Error</p>
        <p className="text-dark-muted dark:text-neutral-400 mt-2">{message}</p>
      </div>
    </div>
  );
}

type ChatTypeFilter = 'all' | 'table' | 'group' | 'direct';

function getConversationType(conversation: AdminConversation): Exclude<ChatTypeFilter, 'all'> {
  if (conversation.tableId) return 'table';
  if (conversation.kind === 'group') return 'group';
  return 'direct';
}

export default function AdminChats() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedConversation, setSelectedConversation] = useState<AdminConversation | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCloseOutOpen, setIsCloseOutOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<ChatTypeFilter>('all');
  // Then listen to conversations (only when authenticated)
  const {
    conversations,
    isLoading: isConversationsLoading,
    error: conversationsError,
  } = useAdminConversationsListener(true);

  const toggleStar = useToggleConversationStar();
  const [togglingStarId, setTogglingStarId] = useState<string | null>(null);
  const deleteAllGroupChats = useDeleteAllGroupChats();
  const toast = useToast();

  const handleDeleteAllGroupChats = async () => {
    if (deleteAllGroupChats.isPending) return;
    if (
      !window.confirm(
        `This will permanently delete all ${groupChatCount} group chat${groupChatCount === 1 ? '' : 's'} and their messages. This cannot be undone.`,
      )
    ) return;
    try {
      const res = await deleteAllGroupChats.mutateAsync();
      if (!res.success) { toast.error(res.error || 'Failed to delete group chats'); return; }
      toast.success(`Deleted ${res.data?.deletedCount ?? groupChatCount} group chat${(res.data?.deletedCount ?? groupChatCount) === 1 ? '' : 's'}`);
      if (selectedConversation?.kind === 'group') handleBack();
    } catch {
      toast.error('Failed to delete group chats');
    }
  };

  // Starred conversations first (by starredAt desc), then the rest by updatedAt desc.
  // Firestore listener emits the authoritative state — local sort only, no optimistic mutation.
  const sortedConversations = useMemo(() => {
    return [...conversations].sort((a, b) => {
      const aStar = a.starred === true;
      const bStar = b.starred === true;
      if (aStar !== bStar) return aStar ? -1 : 1;
      if (aStar && bStar) {
        const aAt = a.starredAt ? new Date(a.starredAt).getTime() : 0;
        const bAt = b.starredAt ? new Date(b.starredAt).getTime() : 0;
        if (aAt !== bAt) return bAt - aAt;
      }
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [conversations]);

  const handleToggleStar = async (conversation: AdminConversation) => {
    setTogglingStarId(conversation.id);
    try {
      await toggleStar.mutateAsync({
        conversationId: conversation.id,
        starred: !(conversation.starred === true),
      });
    } catch (err) {
      console.error('Failed to toggle star:', err);
    } finally {
      setTogglingStarId(null);
    }
  };

  // Fetch user details for all participants

  const { data: userDetailsData } = useAdminChatUserDetails(selectedConversation?.participants || []);
  const userDetails = userDetailsData?.data?.users || {};

  const groupChatCount = useMemo(
    () => sortedConversations.filter((c) => c.kind === 'group').length,
    [sortedConversations],
  );

  // Apply type filter and search query (matches title or subtitle, case-insensitive).
  const filteredSortedConversations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return sortedConversations.filter((c) => {
      if (typeFilter !== 'all' && getConversationType(c) !== typeFilter) return false;
      if (q.length === 0) return true;
      const title = (c.title || '').toLowerCase();
      const subtitle = (c.subtitle || '').toLowerCase();
      return title.includes(q) || subtitle.includes(q);
    });
  }, [sortedConversations, searchQuery, typeFilter]);

  // Sync URL with selected conversation
  const chatIdFromUrl = searchParams.get('chat');
  
  // When conversations load, select the one from URL if present
  // Also handle browser back button by syncing state with URL
  useEffect(() => {
    if (chatIdFromUrl && conversations.length > 0) {
      // URL has a chat ID - select it if different from current
      if (selectedConversation?.id !== chatIdFromUrl) {
        const conversationFromUrl = conversations.find((c) => c.id === chatIdFromUrl);
        if (conversationFromUrl) {
          setSelectedConversation(conversationFromUrl);
        }
      }
    } else if (!chatIdFromUrl && selectedConversation) {
      // URL has no chat ID but we have a selection - clear it (e.g., browser back button)
      setSelectedConversation(null);
    }
  }, [chatIdFromUrl, conversations, selectedConversation]);

  // When selected conversation is updated in the list, update local state
  useEffect(() => {
    if (selectedConversation) {
      const updated = conversations.find((c) => c.id === selectedConversation.id);
      if (updated && updated !== selectedConversation) {
        setSelectedConversation(updated);
      }
    }
  }, [conversations, selectedConversation]);

  // Handle selecting a conversation - update URL
  const handleSelectConversation = (conversation: AdminConversation) => {
    setSelectedConversation(conversation);
    setSearchParams({ chat: conversation.id }, { replace: false });
  };

  // Handle going back - clear URL
  const handleBack = () => {
    setSelectedConversation(null);
    setSearchParams({}, { replace: true });
  };

  const total = conversations.length;

  if (conversationsError) {
    return (
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-2 dark:text-neutral-100">Chats</h1>
          <p className="text-dark-muted dark:text-neutral-400 text-lg">View all user conversations</p>
        </motion.div>
        <ErrorState message="Failed to load conversations. Please try refreshing the page." />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 flex items-start justify-between gap-4 flex-wrap"
      >
        <div>
          <h1 className="text-4xl md:text-5xl font-bold mb-2 dark:text-neutral-100">Chats</h1>
          <p className="text-dark-muted dark:text-neutral-400 text-lg">View all user conversations</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {groupChatCount > 0 && (
            <button
              onClick={handleDeleteAllGroupChats}
              disabled={deleteAllGroupChats.isPending}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-500/10 text-red-400 rounded-lg font-medium hover:bg-red-500/20 transition-colors disabled:opacity-50"
            >
              {deleteAllGroupChats.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Delete all group chats ({groupChatCount})
            </button>
          )}
          <button
            onClick={() => setIsCloseOutOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary-500 text-white rounded-lg font-medium hover:bg-secondary-600 transition-colors"
          >
            <Beer className="w-4 h-4" />
            Close-out chat
          </button>
          <button
            onClick={() => setIsCreateOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New group chat
          </button>
        </div>
      </motion.div>

      {/* Chat Interface */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card p-0 overflow-hidden"
        style={{ height: 'calc(100vh - 350px)', minHeight: '500px' }}
      >
        <div className="flex h-full">
          {/* Conversation List */}
          <AnimatePresence mode="wait">
            {(!selectedConversation || window.innerWidth >= 768) && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className={`${
                  selectedConversation ? 'hidden md:flex' : 'flex'
                } flex-col no-shrink w-full md:w-96 border-r border-dark-border dark:border-neutral-700 h-full overflow-hidden`}
              >
                {/* List Header */}
                <div className="p-4 border-b border-dark-border dark:border-neutral-700 space-y-2">
                  <div className="flex items-center justify-between">
                    <h2 className="font-semibold flex items-center gap-2 dark:text-neutral-100">
                      <MessageCircle className="w-5 h-5 text-primary-500" />
                      All Conversations
                      {isConversationsLoading && (
                        <Loader2 className="w-4 h-4 text-primary-500 animate-spin" />
                      )}
                    </h2>
                    <span className="text-xs text-dark-muted dark:text-neutral-500">
                      {filteredSortedConversations.length}
                      {filteredSortedConversations.length !== total && ` / ${total}`}
                    </span>
                  </div>
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-dark-muted dark:text-neutral-500 pointer-events-none" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by name or subtitle"
                      className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg bg-dark-border/30 dark:bg-neutral-700 border border-dark-border/50 dark:border-neutral-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:text-neutral-100 dark:placeholder-neutral-400"
                    />
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    {(
                      [
                        { value: 'all', label: 'All' },
                        { value: 'table', label: 'Table' },
                        { value: 'group', label: 'Group' },
                        { value: 'direct', label: 'Direct' },
                      ] as { value: ChatTypeFilter; label: string }[]
                    ).map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setTypeFilter(opt.value)}
                        className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                          typeFilter === opt.value
                            ? 'bg-primary-500 text-white'
                            : 'bg-dark-border/30 dark:bg-neutral-700 text-dark-muted dark:text-neutral-300 hover:bg-dark-border/50 dark:hover:bg-neutral-600'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto">
                  {isConversationsLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
                    </div>
                  ) : filteredSortedConversations.length === 0 ? (
                    <div className="text-center text-dark-muted dark:text-neutral-400 py-8">
                      {sortedConversations.length === 0 ? 'No conversations found' : 'No conversations match your filters'}
                    </div>
                  ) : (
                    filteredSortedConversations.map((conversation) => (
                      <ConversationItem
                        key={conversation.id}
                        conversation={conversation}
                        isSelected={selectedConversation?.id === conversation.id}
                        userDetails={userDetails || {}}
                        onSelect={() => handleSelectConversation(conversation)}
                        onToggleStar={() => handleToggleStar(conversation)}
                        isTogglingStar={togglingStarId === conversation.id}
                      />
                    ))
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Conversation View */}
          <AnimatePresence mode="wait">
            {selectedConversation ? (
              <motion.div
                key={selectedConversation.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="flex-1 overflow-hidden"
              >
                <ConversationView
                  conversation={selectedConversation}
                  userDetails={userDetails}
                  onBack={handleBack}
                />
              </motion.div>
            ) : (
              <EmptyState />
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {isCreateOpen && (
        <CreateGroupChatModal
          onClose={() => setIsCreateOpen(false)}
          onCreated={(conversationId) => {
            setSearchParams({ chat: conversationId }, { replace: false });
          }}
        />
      )}

      {isCloseOutOpen && (
        <CreateCloseOutChatModal
          onClose={() => setIsCloseOutOpen(false)}
          onCreated={(conversationId) => {
            setSearchParams({ chat: conversationId }, { replace: false });
          }}
        />
      )}
    </div>
  );
}
